import { useLicensingServiceV1 } from "@rw1/api-services";
import { Licensing_V1 } from "@rw1/auto-generated-clients";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useFetchAll } from "../../../hooks/useFetchAll";

export interface IncomingLicense {
  licenseId: string;
  releaseChannelId: string;
  seats: number | null;
  allocatedSeats: number;
  expiresAt: Date | null;
  licenseName: string;
  issuingOrganizationId: string;
  hasWhitelist: boolean;
}

export const INCOMING_LICENSES_QUERY_KEY = "incoming-licenses";

export const useIncomingLicenses = (): {
  licensesByReleaseChannel: Map<string, IncomingLicense>;
  isLoading: boolean;
} => {
  const licensingService = useLicensingServiceV1();
  const { companyId } = useParams();

  const { data: licenses = [], isLoading } = useFetchAll<Licensing_V1.License>(
    [INCOMING_LICENSES_QUERY_KEY, companyId],
    (page, size) =>
      licensingService.getAllByOrganizationId(companyId!, page, size),
    { enabled: !!companyId },
  );

  const licensesByReleaseChannel = useMemo(() => {
    const map = new Map<string, IncomingLicense>();

    if (!companyId || !licenses.length) return map;

    // Filter licenses granted to us (where we are the assigned organization)
    const incomingLicenses = licenses.filter(
      (license) => license.assignedOrganizationId === companyId,
    );

    for (const license of incomingLicenses) {
      if (license.releaseChannelId) {
        map.set(license.releaseChannelId, {
          licenseId: license.licenseId,
          releaseChannelId: license.releaseChannelId,
          seats: license.seats ?? null,
          allocatedSeats: license.allocatedSeats,
          expiresAt: license.expiresAt ?? null,
          licenseName: license.licenseName,
          issuingOrganizationId: license.issuingOrganizationId,
          hasWhitelist: license.hasWhitelist,
        });
      }
    }

    return map;
  }, [companyId, licenses]);

  return { licensesByReleaseChannel, isLoading };
};
